import React, { useEffect, useMemo, useState } from "react";
import { ReactComponent as CloseSvg } from "../../assets/icons/close.svg";
import {
  ModalContainer,
  CloseButton,
  ModalContent,
  ModalTitle,
  Row,
  ContentContainer,
  Content,
  EmptyState,
  Column,
  ModalFooter,
  SubmitButton,
  ButtonsContainer,
  CancelButton,
} from "./GalleryModal.styles";
import Modal from "../utils/Modal/Modal";
import { useLockBodyScroll } from "../../hooks/useLockBodyScroll";
import { useDispatch, useSelector } from "react-redux";
import {
  selectGalleryData,
  selectGalleryDataPending,
} from "../../store/slices/model/slice";
import { fetchDefaultGallery } from "../../store/slices/model/asyncThunks";
import { useMediaQuery } from "react-responsive";
import VideoMasonrySkeletonLoader from "../utils/VideoMasonrySkeletonLoader";
import GalleryImage from "./GalleryImage";
import { toast } from "../modern-ui/sonner";

const GalleryModal = ({
  isOpen,
  onClose,
  onConfirm,
  selectedImages,
  uploadedImagesCount,
}) => {
  useLockBodyScroll(isOpen);

  const isDesktop = useMediaQuery({ maxWidth: 1224 });
  const isTable = useMediaQuery({ maxWidth: 950 });
  const isMobile = useMediaQuery({ maxWidth: 768 });

  const numOfColumns = useMemo(() => {
    switch (true) {
      case isMobile:
        return 2;
      case isTable:
        return 3;
      case isDesktop:
        return 4;
      default:
        return 5;
    }
  }, [isDesktop, isTable, isMobile]);

  const dispatch = useDispatch();
  const [addedImages, setAddedImages] = useState(selectedImages);

  const data = useSelector(selectGalleryData);
  const loading = useSelector(selectGalleryDataPending);

  useEffect(() => {
    setAddedImages(selectedImages);
  }, [selectedImages]);

  useEffect(() => {
    dispatch(fetchDefaultGallery());
  }, [dispatch]);

  const handleCancel = () => {
    onClose();
  };

  const selectedCountText = isMobile
    ? ""
    : `(${uploadedImagesCount + addedImages.length}/3-6)`;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      customOverlayStyles={{ zIndex: 3100 }}
    >
      <ModalContainer>
        <ModalContent>
          <Row>
            <ModalTitle>{`Select Testing Image (${
              uploadedImagesCount + addedImages.length
            }/6-12)`}</ModalTitle>
            <CloseButton onClick={handleCancel}>
              <CloseSvg width={20} height={20} />
            </CloseButton>
          </Row>

          <ContentContainer>
            {loading || !data ? (
              <VideoMasonrySkeletonLoader count={24} />
            ) : (
              <Content>
                {!data.length ? (
                  <EmptyState>
                    <h3>No videos found</h3>
                    <p>There are no videos to display at the moment.</p>
                  </EmptyState>
                ) : (
                  Array.from({ length: numOfColumns }).map((_, colIndex) => {
                    return (
                      <Column key={colIndex} $numOfColumns={numOfColumns}>
                        {data
                          .filter((_, i) => i % numOfColumns === colIndex)
                          .map((url, i) => {
                            const baseUrl = url.split("?")[0];
                            return (
                              <GalleryImage
                                key={url}
                                url={url}
                                isAdded={addedImages.some(
                                  (u) => u.split("?")[0] === baseUrl
                                )}
                                onToggle={() => {
                                  setAddedImages((prev) => {
                                    const exists = prev.some(
                                      (u) => u.split("?")[0] === baseUrl
                                    );
                                    if (exists) {
                                      return prev.filter(
                                        (u) => u.split("?")[0] !== baseUrl
                                      );
                                    } else {
                                      return [...prev, url];
                                    }
                                  });
                                }}
                              />
                            );
                          })}
                      </Column>
                    );
                  })
                )}
              </Content>
            )}
          </ContentContainer>
          <ModalFooter>
            <SubmitButton
              onClick={() => {
                setAddedImages([]);
              }}
            >
              Clear All
            </SubmitButton>
            <ButtonsContainer>
              {!isMobile && (
                <CancelButton
                  onClick={() => {
                    onClose();
                  }}
                >
                  Cancel
                </CancelButton>
              )}
              <SubmitButton
                onClick={() => {
                  if (uploadedImagesCount + addedImages.length > 6) {
                    toast.error("Please select max 6 images");
                  } else {
                    onConfirm(addedImages);
                    onClose();
                  }
                }}
              >{`Confirm ${selectedCountText}`}</SubmitButton>
            </ButtonsContainer>
          </ModalFooter>
        </ModalContent>
      </ModalContainer>
    </Modal>
  );
};

export default GalleryModal;
